import { useSearchParams } from 'react-router-dom';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { CdnOnboardingGapsTab } from './cdn/cdn-onboarding-gaps-tab';
import { CdnConnectionsTab } from './cdn/cdn-connections-tab';

const TAB_VALUES = ['gaps', 'connections'] as const;
type TabValue = (typeof TAB_VALUES)[number];

/**
 * Operations → CDN — one place for the two CDN-vendor ops workflows, split into
 * tabs (previously two separate nav entries):
 *  - **Onboarding Gaps** — onboard form (bind a domain to a CDN service) + the
 *    detected-gap fleet list from the onboarding-gap detector.
 *  - **Connections** — Eagle CDN services and whether each has its own console
 *    account/credential; per-row Connect mints the missing one.
 * Active tab persisted in `?tab=`. Inactive tab is unmounted so it doesn't fetch.
 */
export default function OperationsCdnPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const requestedTab = searchParams.get('tab') as TabValue | null;
  const activeTab: TabValue =
    requestedTab && (TAB_VALUES as readonly string[]).includes(requestedTab)
      ? requestedTab
      : 'gaps';

  const onTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <Container size="full">
      <PageHeader
        title="CDN"
        description="Onboard domains serving at the vendor and manage Eagle CDN service connections."
        style={{ marginBottom: 'var(--vn-space-4)' }}
      />

      <Tabs value={activeTab} onValueChange={onTabChange}>
        <TabsList>
          <TabsTrigger value="gaps">Onboarding Gaps</TabsTrigger>
          <TabsTrigger value="connections">Connections</TabsTrigger>
        </TabsList>

        <TabsContent value="gaps">
          {activeTab === 'gaps' ? <CdnOnboardingGapsTab /> : null}
        </TabsContent>

        <TabsContent value="connections">
          {activeTab === 'connections' ? <CdnConnectionsTab /> : null}
        </TabsContent>
      </Tabs>
    </Container>
  );
}
